/**
 * Where the anonymous visitor id lives.
 *
 * It used to live only in `localStorage`, which is scoped to one ORIGIN. That
 * is the wrong scope for the thing it identifies. A visitor who clicks an ad on
 * `www.example.com` and signs up on `app.example.com` is one person, but
 * `localStorage` on the second origin is empty, so a second id is minted and
 * the two halves of that visit can never be joined. The ad that produced the
 * customer stays invisible — not because the data is missing, but because it is
 * filed under a name nobody can look up.
 *
 * A cookie on the registrable domain (`.example.com`) is visible to every
 * subdomain, which is the scope a "visitor" actually has. It also gets SENT
 * with every request to that domain — so a business whose signup runs on the
 * server can read the visitor id off the request and identify without the SDK
 * running on that page at all.
 *
 * `localStorage` is still read and still written: every visitor already out
 * there has their id only in localStorage, and dropping it would orphan all of
 * them at once. Read cookie → read localStorage → generate; write both.
 */

/**
 * Must equal `VISITOR_ID_COOKIE` in @hamla/shared, which is what the SERVER
 * reads off the incoming request. Two spellings would fail silently and
 * completely — every signup unattributed, nothing in a log to see.
 *
 * Deliberately a literal, not an import. Importing the shared constant pulls
 * the whole package into the browser bundles: it took the 1KB early beacon to
 * 506KB. A test asserts the two strings match instead — same protection, no
 * bundle cost.
 */
export const VISITOR_ID_KEY = 'hamla_visitor_id';

/** Two years — long enough that a returning visitor is still the same person. */
const MAX_AGE_SECONDS = 63072000;

export function readCookie(name: string): string | null {
  try {
    for (const part of document.cookie.split(';')) {
      const eq = part.indexOf('=');
      if (eq < 0) continue;
      if (part.slice(0, eq).trim() !== name) continue;
      const value = decodeURIComponent(part.slice(eq + 1).trim());
      if (value) return value;
    }
  } catch {
    // document.cookie throws in sandboxed iframes.
  }
  return null;
}

/**
 * The broadest domain this page is allowed to set a cookie on.
 *
 * There is no public-suffix list in the browser, so `example.co.uk` cannot be
 * told apart from `co.uk` by counting dots — and a cookie on `.co.uk` would be
 * rejected. So ask the browser instead: walk up from the host one label at a
 * time, try to set a probe cookie, and keep the broadest one that actually
 * stuck. The browser already enforces the public-suffix rule, so whatever it
 * accepts is by definition legal.
 *
 * Returns null for hosts that can't carry a domain cookie at all (localhost,
 * bare IPs) — those get a host-only cookie, which is correct for them.
 */
let registrableDomain: string | null | undefined;

function findRegistrableDomain(): string | null {
  // The answer cannot change while the page is open, and the session cookie is
  // rewritten as the visitor moves around — probe once, not on every write.
  if (registrableDomain !== undefined) return registrableDomain;
  registrableDomain = probeRegistrableDomain();
  return registrableDomain;
}

function probeRegistrableDomain(): string | null {
  try {
    const host = window.location.hostname;
    // An IP address or a single-label host has no parent to share with.
    if (!host.includes('.') || /^[\d.]+$/.test(host)) return null;

    const labels = host.split('.');
    const probe = `__hamla_probe_${Date.now().toString(36)}`;
    // Start narrow (`.b.c`) and widen (`.a.b.c` → `.b.c` → `.c`); the last one
    // that sticks is the broadest legal scope.
    for (let i = labels.length - 2; i >= 0; i--) {
      const candidate = labels.slice(i).join('.');
      document.cookie = `${probe}=1; domain=.${candidate}; path=/; SameSite=Lax`;
      if (readCookie(probe) !== null) {
        // Clean up the probe on the domain that accepted it.
        document.cookie = `${probe}=; domain=.${candidate}; path=/; max-age=0`;
        return candidate;
      }
    }
  } catch {
    // Fall through to a host-only cookie.
  }
  return null;
}

/**
 * A first-party cookie on the registrable domain, so every subdomain shares it.
 * `maxAgeSeconds` defaults to the visitor id's two years.
 */
export function writeCookie(name: string, value: string, maxAgeSeconds = MAX_AGE_SECONDS): void {
  try {
    const domain = findRegistrableDomain();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // Lax, not Strict: a visitor arriving from an ad is a cross-site
    // navigation, and Strict would withhold the cookie on exactly that request.
    document.cookie =
      `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}` +
      `; SameSite=Lax${secure}${domain ? `; domain=.${domain}` : ''}`;
  } catch {
    // Cookies disabled — localStorage still carries the id for this origin.
  }
}

export function readLocal(name: string): string | null {
  try {
    return localStorage.getItem(name) || null;
  } catch {
    return null;
  }
}

export function writeLocal(name: string, value: string): void {
  try {
    localStorage.setItem(name, value);
  } catch {
    // Safari private mode throws on write.
  }
}

function generateVisitorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `vis-${timestamp}-${random}`;
}

/**
 * Read the stored id without creating one.
 *
 * For callers that run outside SDK init (the early beacon, form rendering) and
 * must not mint an id of their own — two stores disagreeing is the bug this
 * file exists to fix.
 */
export function peekVisitorId(): string | null {
  return readCookie(VISITOR_ID_KEY) ?? readLocal(VISITOR_ID_KEY);
}

/**
 * The domain a first-party cookie on this site actually lives on.
 *
 * Reported to the server because it is the one thing only the BROWSER can
 * answer. Meta's `fbc` encodes a subdomain index — `com`=0, `example.com`=1,
 * `www.example.com`=2 — counted on the domain the click cookie sits on. When we
 * rebuild an `fbc` server-side we have to state that index, and deriving it
 * from a hostname needs a public-suffix list: `shop.co.uk` is three labels but
 * one registrable domain, and `shop.sa` is two. Counting dots gets the first
 * wrong.
 *
 * The browser already enforces the public-suffix rule when it accepts or
 * rejects a domain cookie, so the probe above answers it exactly, for free, and
 * for every TLD — including ones no bundled list would have.
 *
 * Null for hosts that cannot carry a domain cookie (localhost, bare IPs), which
 * is honest rather than a guess.
 */
export function resolveCookieDomain(): string | null {
  return findRegistrableDomain();
}

/** Persist an id to both stores, so whichever survives still identifies them. */
export function persistVisitorId(visitorId: string): void {
  writeCookie(VISITOR_ID_KEY, visitorId);
  writeLocal(VISITOR_ID_KEY, visitorId);
}

/**
 * The visitor id for this browser, creating one on first sight.
 *
 * `urlVisitorId` wins when present — an off-site link (email, SMS, push) names
 * the visitor explicitly, and that is better evidence than anything stored.
 */
export function resolveVisitorId(urlVisitorId?: string | null): string {
  if (urlVisitorId) {
    persistVisitorId(urlVisitorId);
    return urlVisitorId;
  }

  // Cookie first: it is the one that spans subdomains, so preferring it makes
  // every origin converge on a single id.
  const fromCookie = readCookie(VISITOR_ID_KEY);
  if (fromCookie) {
    // Existing visitors have it in localStorage on their landing origin but not
    // yet on this one. Writing it back costs nothing and keeps the two stores
    // from disagreeing.
    writeLocal(VISITOR_ID_KEY, fromCookie);
    return fromCookie;
  }

  // No cookie: either a first visit, or a visitor from before the cookie
  // existed. Adopt their localStorage id rather than minting a new one —
  // otherwise every returning visitor loses their history the day this ships.
  const fromLocal = readLocal(VISITOR_ID_KEY);
  if (fromLocal) {
    writeCookie(VISITOR_ID_KEY, fromLocal);
    return fromLocal;
  }

  const generated = generateVisitorId();
  persistVisitorId(generated);
  return generated;
}
